import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, GitBranch, Compass } from "lucide-react";

const T = {
  fr: {
    eyebrow: "En trois souffles", title: "Comment ça marche",
    steps: [
      { t: "Une curiosité t'appelle", d: "Un mot, une image, une question qui te trotte dans la tête. Aucun programme, aucune note — juste ton envie." },
      { t: "Tu tires le fil", d: "L'Élan, ton précepteur IA, t'ouvre des portes : débats, lexiques, jeux, concepts illustrés. Tu explores à ton rythme." },
      { t: "Tu vas plus loin que prévu", d: "De fil en fil, tu relies les idées et tu retiens sans effort. La connaissance devient une aventure, pas une corvée." },
    ],
  },
  en: {
    eyebrow: "In three breaths", title: "How it works",
    steps: [
      { t: "A curiosity calls you", d: "A word, an image, a question stuck in your head. No syllabus, no grades — just your desire." },
      { t: "You pull the thread", d: "L'Élan, your AI tutor, opens doors: debates, lexicons, games, illustrated concepts. You explore at your own pace." },
      { t: "You go further than expected", d: "Thread by thread, you connect ideas and remember effortlessly. Knowledge becomes an adventure, not a chore." },
    ],
  },
  es: {
    eyebrow: "En tres respiros", title: "Cómo funciona",
    steps: [
      { t: "Una curiosidad te llama", d: "Una palabra, una imagen, una pregunta que te ronda. Sin programa, sin notas — solo tu deseo." },
      { t: "Tiras del hilo", d: "L'Élan, tu preceptor IA, te abre puertas: debates, léxicos, juegos, conceptos ilustrados. Exploras a tu ritmo." },
      { t: "Llegas más lejos de lo previsto", d: "De hilo en hilo, conectas ideas y recuerdas sin esfuerzo. El saber se vuelve aventura, no una tarea." },
    ],
  },
  nl: {
    eyebrow: "In drie ademteugen", title: "Hoe het werkt",
    steps: [
      { t: "Een nieuwsgierigheid roept je", d: "Een woord, een beeld, een vraag die blijft hangen. Geen programma, geen cijfers — alleen jouw verlangen." },
      { t: "Je trekt aan de draad", d: "L'Élan, je AI-mentor, opent deuren: debatten, lexicons, spellen, geïllustreerde concepten. Jij verkent op je eigen tempo." },
      { t: "Je komt verder dan gedacht", d: "Draad na draad verbind je ideeën en onthoud je moeiteloos. Kennis wordt een avontuur, geen karwei." },
    ],
  },
};

const ICONS = [Sparkles, GitBranch, Compass];

export function CommentCaMarche() {
  const { i18n } = useTranslation();
  const c = T[(i18n.language || "fr").slice(0, 2)] || T.fr;

  return (
    <section className="relative px-6 my-20" data-testid="comment-ca-marche">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-primary/80 uppercase tracking-[0.28em] text-xs mb-3">{c.eyebrow}</p>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-5xl leading-tight pb-[0.08em] mb-14">{c.title}</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {c.steps.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="glass frame-pulse rounded-3xl border border-primary/12 p-8 text-left relative"
                data-testid={`ccm-step-${i}`}
              >
                <span className="absolute top-6 right-6 z-20 w-11 h-11 rounded-full border border-primary/60 bg-background/90 backdrop-blur-sm flex items-center justify-center select-none shadow-[0_0_16px_-3px_hsl(28_96%_56%_/_0.6)]" data-testid={`ccm-step-${i}-num`}>
                  <span className="font-display text-xl text-gradient-orange" style={{ lineHeight: 1.25, paddingBottom: "0.08em" }}>{i + 1}</span>
                </span>
                <span className="w-12 h-12 rounded-2xl bg-primary/12 border border-primary/25 flex items-center justify-center text-primary mb-5">
                  <Icon size={22} strokeWidth={2} />
                </span>
                <h3 className="font-display text-xl text-foreground/90 mb-3">{s.t}</h3>
                <p className="text-sm text-foreground/60 leading-relaxed">{s.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
